import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from '@capacitor/app';
import { API_BASE, isNative, platform } from './platform';
import { openExternal } from './native-push';
import {
  downloadAndInstallUpdate,
  getInstalledVersionCode,
  supportsInAppUpdate,
} from './native-updater';

export const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.0';
export const APP_VERSION_CODE =
  Number(import.meta.env.VITE_APP_VERSION_CODE ?? '0') || 0;

export interface AppUpdateInfo {
  version: string;
  versionCode: number;
  url: string;
  builtAt?: string | null;
}

function appOrigin(): string {
  if (API_BASE.startsWith('http')) {
    try {
      return new URL(API_BASE).origin;
    } catch {
      // fall through
    }
  }
  return window.location.origin;
}

export async function checkAppUpdate(): Promise<AppUpdateInfo | null> {
  if (!isNative()) return null;
  const origin = appOrigin();
  const response = await fetch(
    `${origin}/downloads/version.json?t=${Date.now()}`,
    { cache: 'no-store' },
  );
  if (!response.ok) return null;
  const info = (await response.json()) as {
    version?: string;
    versionCode?: number;
    url?: string;
    builtAt?: string | null;
  };
  const versionCode = Number(info.versionCode ?? 0);
  const installedVersionCode =
    (await getInstalledVersionCode()) ?? APP_VERSION_CODE;
  if (!versionCode || versionCode <= installedVersionCode) return null;
  const url = info.url ?? '/downloads/family-pco.apk';
  return {
    version: info.version ?? String(versionCode),
    versionCode,
    url: /^https?:\/\//.test(url) ? url : `${origin}${url}`,
    builtAt: info.builtAt ?? null,
  };
}

export function useAppUpdate() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['app-update'],
    queryFn: checkAppUpdate,
    enabled: isNative() && platform() === 'android',
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  useEffect(() => {
    if (!isNative() || platform() !== 'android') return;
    let handle: { remove(): Promise<void> } | null = null;
    let disposed = false;
    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        void queryClient.invalidateQueries({ queryKey: ['app-update'] });
      }
    })
      .then((listener) => {
        if (disposed) void listener.remove();
        else handle = listener;
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
      if (handle) void handle.remove();
    };
  }, [queryClient]);

  return query;
}

export async function openAppUpdate(info: AppUpdateInfo): Promise<void> {
  if (supportsInAppUpdate()) {
    await downloadAndInstallUpdate(info.url);
    return;
  }
  await openExternal(info.url);
}
